import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API_BASE_URL, SETUP_2FA, GET_QR_CODE, SEND_SMS_CODE } from '../apiServices/api_routes';
import { GetUserData, setWorkspaceSelectionRequired, logoutWithAudit } from '../redux/usersSlice';
import { setProjectImg } from '../redux/projectDataSlice';
import { setDocs } from '../redux/documentsSlice';
import { SpinnerIos16Regular } from '@fluentui/react-icons';
import CountryCodeSelector from '../components/CommonComponents/CountryCodeSelector';
import Logo from '../assets/longLogoBright.png';
import styles from '../components/Register/css/Register.module.css';
import loginStyles from '../components/Login/css/Login.module.css';
import Pageheader from '../utils/headerHelment';

const Setup2FAPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { user } = useSelector(state => state.user);
    const [twoFAMethod, setTwoFAMethod] = useState('qr');
    const [smsStep, setSmsStep] = useState('phone');
    const [loading, setLoading] = useState(false);
    const [qrVerificationCode, setQrVerificationCode] = useState(['', '', '', '', '', '']);
    const [smsVerificationCode, setSmsVerificationCode] = useState(['', '', '', '', '', '']);
    const [smsData, setSmsData] = useState({ code: '', number: '' });
    const [resendSecondsRemaining, setResendSecondsRemaining] = useState(0);
    const [setupLockoutSecondsRemaining, setSetupLockoutSecondsRemaining] = useState(0);
    const [showSetupLockScreen, setShowSetupLockScreen] = useState(false);
    const [qrData, setQrData] = useState('');
    const [qrLoading, setQrLoading] = useState(false);
    const qrInputRefs = useRef([]);
    const smsInputRefs = useRef([]);
    const isResendDisabled = resendSecondsRemaining > 0;
    const isSetupLocked = setupLockoutSecondsRemaining > 0;
    const setupLockoutMinutes = Math.floor(setupLockoutSecondsRemaining / 60);
    const setupLockoutSeconds = setupLockoutSecondsRemaining % 60;
    const setupLockoutCountdown = `${setupLockoutMinutes}:${String(setupLockoutSeconds).padStart(2, '0')}`;

    useEffect(() => {
        if (resendSecondsRemaining <= 0) return undefined;
        const intervalId = window.setInterval(() => {
            setResendSecondsRemaining(prev => (prev > 0 ? prev - 1 : 0));
        }, 1000);
        return () => window.clearInterval(intervalId);
    }, [resendSecondsRemaining]);

    useEffect(() => {
        if (!isSetupLocked) return undefined;
        const intervalId = window.setInterval(() => {
            setSetupLockoutSecondsRemaining(prev => (prev > 0 ? prev - 1 : 0));
        }, 1000);
        return () => window.clearInterval(intervalId);
    }, [isSetupLocked]);

    const handleSetupLockoutError = error => {
        if (error?.response?.status !== 423) return false;
        const seconds = Number(error?.response?.data?.retry_after_seconds);
        const remaining = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 5 * 60;
        setSetupLockoutSecondsRemaining(remaining);
        setShowSetupLockScreen(true);
        toast.error('Too many invalid verification attempts. Please try again later.');
        return true;
    };

    useEffect(() => {
        if (!Cookies.get('refresh')) {
            navigate('/login', { replace: true });
            return;
        }
        if (!user?.email) {
            dispatch(GetUserData());
        }
    }, [navigate, dispatch, user?.email]);

    useEffect(() => {
        if (!user?.email) return;
        if (user?.has_mfa) {
            navigate('/project', { replace: true });
            return;
        }
    }, [user, navigate]);

    const fetchQrCode = async () => {
        if (!user?.email) return;
        try {
            setQrLoading(true);
            const response = await axios.post(`${API_BASE_URL}${GET_QR_CODE}`, { email: user.email });
            setQrData(response.data.qr_data);
        } catch (error) {
            toast.error('Failed to fetch QR code');
        } finally {
            setQrLoading(false);
        }
    };

    useEffect(() => {
        if (twoFAMethod === 'qr' && user?.email) fetchQrCode();
    }, [twoFAMethod, user?.email]);

    const handleQrCodeChange = (index, e) => {
        const newCode = [...qrVerificationCode];
        newCode[index] = e.target.value;
        setQrVerificationCode(newCode);
        if (e.target.value && index < 5) qrInputRefs.current[index + 1]?.focus();
    };

    const handleQrKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !qrVerificationCode[index] && index > 0) {
            qrInputRefs.current[index - 1]?.focus();
        }
    };

    const handleQrPaste = e => {
        const pasted = e.clipboardData.getData('Text');
        if (pasted.length === 6 && qrInputRefs.current[0] === document.activeElement) {
            setQrVerificationCode(pasted.split('').slice(0, 6));
        }
    };

    const handleSmsCodeChange = (index, e) => {
        const newCode = [...smsVerificationCode];
        newCode[index] = e.target.value;
        setSmsVerificationCode(newCode);
        if (e.target.value && index < 5) smsInputRefs.current[index + 1]?.focus();
    };

    const handleSmsKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !smsVerificationCode[index] && index > 0) {
            smsInputRefs.current[index - 1]?.focus();
        }
    };

    const handleSmsPaste = e => {
        const pasted = e.clipboardData.getData('Text');
        if (pasted.length === 6 && smsInputRefs.current[0] === document.activeElement) {
            setSmsVerificationCode(pasted.split('').slice(0, 6));
        }
    };

    const handleSmsChange = (field, value) => setSmsData(prev => ({ ...prev, [field]: value }));

    const handleQrActivation = async () => {
        const code = qrVerificationCode.join('');
        if (code.length !== 6) {
            toast.error('Please enter a 6-digit verification code');
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API_BASE_URL}${SETUP_2FA}`, {
                email: user.email,
                mfa_method: 'app',
                verification_code: code,
            });
            toast.success('2FA setup completed successfully! Please sign in again.');
            localStorage.removeItem('persist:root');
            Cookies.set('refresh', '');
            dispatch(setProjectImg(''));
            dispatch(setDocs([]));
            dispatch(setWorkspaceSelectionRequired(false));
            await dispatch(logoutWithAudit());
            const invite = new URLSearchParams(location.search).get('invite');
            navigate(invite ? `/login?invite=${encodeURIComponent(invite)}` : '/login', { replace: true });
        } catch (error) {
            if (handleSetupLockoutError(error)) return;
            toast.error(error.response?.data?.error || 'Failed to activate 2FA');
        } finally {
            setLoading(false);
        }
    };

    const handleSendSmsCode = async () => {
        if (!smsData.code || !smsData.number) {
            toast.error('Please enter your phone number');
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API_BASE_URL}${SEND_SMS_CODE}`, {
                email: user.email,
                phone_number: smsData.code + smsData.number,
            });
            setSmsStep('verification');
            setResendSecondsRemaining(30);
            toast.success('Verification code sent to your phone!');
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to send verification code.');
        } finally {
            setLoading(false);
        }
    };

    const handleSmsActivation = async () => {
        const code = smsVerificationCode.join('');
        if (code.length !== 6) {
            toast.error('Please enter a 6-digit verification code');
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API_BASE_URL}${SETUP_2FA}`, {
                email: user.email,
                mfa_method: 'sms_twilio',
                phone_number: smsData.code + smsData.number,
                verification_code: code,
            });
            toast.success('2FA setup completed successfully! Please sign in again.');
            localStorage.removeItem('persist:root');
            Cookies.set('refresh', '');
            dispatch(setProjectImg(''));
            dispatch(setDocs([]));
            dispatch(setWorkspaceSelectionRequired(false));
            await dispatch(logoutWithAudit());
            const invite = new URLSearchParams(location.search).get('invite');
            navigate(invite ? `/login?invite=${encodeURIComponent(invite)}` : '/login', { replace: true });
        } catch (error) {
            if (handleSetupLockoutError(error)) return;
            toast.error(error.response?.data?.error || 'Invalid verification code.');
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        if (twoFAMethod === 'sms' && smsStep === 'verification') setSmsStep('phone');
        else if (twoFAMethod === 'sms' && smsStep === 'phone') setTwoFAMethod('qr');
    };

    const otpInputStyle = {
        width: '14%',
        height: '48px',
        fontSize: '18px',
        fontFamily: 'monospace',
        fontWeight: 'bold',
        border: 'none',
        borderBottom: '1px solid var(--Neutral-Gray-6)',
        outline: 'none',
        textAlign: 'center',
        borderRadius: '0px',
    };

    const qrContainerStyle = {
        width: '168px',
        height: '168px',
        backgroundColor: '#f8f9fa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid #dee2e6',
        borderRadius: '8px',
    };

    const qrImageContainerStyle = {
        width: '168px',
        height: '168px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid #dee2e6',
        borderRadius: '8px',
        padding: '8px',
    };

    if (!user?.email) return null;

    const renderContent = () => {
        if (twoFAMethod === 'qr') {
            return (
                <>
                    <h5 className={styles.formLabel}>Set up 2FA</h5>
                    <p className={styles.formSubtitle}>
                        For heightened security measures, kindly scan the provided code using your authenticator app and enter the corresponding verification code below. We will
                        use this method by default.
                    </p>
                    <div className="text-center">
                        <div className="d-flex justify-content-center mb-3">
                            {qrLoading ? (
                                <div style={qrContainerStyle}>
                                    <SpinnerIos16Regular className="rotateLoader" />
                                </div>
                            ) : qrData ? (
                                <div style={qrImageContainerStyle}>
                                    <img
                                        src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(qrData)}`}
                                        alt="QR Code"
                                        style={{ width: '100%', height: '100%' }}
                                    />
                                </div>
                            ) : (
                                <div style={qrContainerStyle}>
                                    <span className="text-muted">QR Code</span>
                                </div>
                            )}
                        </div>
                        <p className="text-muted small">Open your authenticator app and scan this QR code to add your Lens account.</p>
                    </div>
                    <div className="mb-3">
                        <label className={styles.fieldLabel}>Enter verification code</label>
                        <div className="d-flex justify-content-center">
                            <div className="d-flex" style={{ gap: '8px', justifyContent: 'space-between', width: '100%' }}>
                                {[0, 1, 2, 3, 4, 5].map(i => (
                                    <input
                                        key={i}
                                        type="text"
                                        maxLength="1"
                                        value={qrVerificationCode[i]}
                                        onChange={e => handleQrCodeChange(i, e)}
                                        onKeyDown={e => handleQrKeyDown(i, e)}
                                        ref={r => (qrInputRefs.current[i] = r)}
                                        onPaste={handleQrPaste}
                                        className={'form-control text-center ' + styles.inputField}
                                        style={otpInputStyle}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="d-flex align-items-center justify-content-between mt-4">
                        <button onClick={handleQrActivation} disabled={loading || qrVerificationCode.join('').length !== 6} className={styles.submitBtn}>
                            Activate account {loading && <SpinnerIos16Regular className="rotateLoader ms-1" />}
                        </button>
                        <span role="button" onClick={() => setTwoFAMethod('sms')} className={styles.secondaryBtnLink}>
                            Use SMS authentication
                        </span>
                    </div>
                </>
            );
        }

        if (twoFAMethod === 'sms' && smsStep === 'phone') {
            return (
                <>
                    <h5 className={styles.formLabel}>Set up 2FA</h5>
                    <p className={styles.formSubtitle}>
                        For heightened security measures, add your mobile number below to receive verification code. We will use this method by default. Your mobile number will only
                        be used for account security. Standard SMS charges will apply.
                    </p>
                    <div className="d-flex">
                        <div style={{ width: '120px' }} className="me-2">
                            <p className={styles.fieldLabel}>Code</p>
                            <CountryCodeSelector value={smsData?.code} onChange={v => handleSmsChange('code', v)} placeholder="Please Select" name="code" ariaLabel="Country code" />
                        </div>
                        <div className="w-100">
                            <p className={styles.fieldLabel}>Mobile Number</p>
                            <input
                                type="number"
                                placeholder="Mobile Number"
                                value={smsData?.number}
                                onChange={e => handleSmsChange('number', e.target.value)}
                                className={'form-control ' + styles.inputField}
                            />
                        </div>
                    </div>
                    <div className="d-flex align-items-center justify-content-between mt-4">
                        <button onClick={handleSendSmsCode} disabled={!smsData?.code || !smsData?.number || loading} className={styles.submitBtn}>
                            Send Code {loading && <SpinnerIos16Regular className="rotateLoader ms-1" />}
                        </button>
                        <span role="button" onClick={handleBack} className={styles.secondaryBtnLink}>
                            Back
                        </span>
                    </div>
                </>
            );
        }

        return (
            <>
                {showSetupLockScreen ? (
                    <>
                        <h5 className={styles.formLabel}>Your Account Is Temporarily Locked</h5>
                        <p className={styles.formSubtitle}>
                            For security reasons, your account has been temporarily locked after multiple unsuccessful verification attempts.
                            Please try signing in again in {setupLockoutCountdown}.
                        </p>
                        <div className="d-flex align-items-center justify-content-between mt-4">
                            <button
                                type="button"
                                onClick={() => setShowSetupLockScreen(false)}
                                disabled={isSetupLocked}
                                className={styles.submitBtn}
                                style={isSetupLocked ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}>
                                Sign-in
                            </button>
                            <a href="mailto:LensSupport@beyond-ma.com" className={styles.secondaryBtnLink}>
                                Contact Support
                            </a>
                        </div>
                    </>
                ) : (
                    <>
                <h5 className={styles.formLabel}>Enter verification code</h5>
                <p className={styles.formSubtitle}>The verification code is sent on your mobile number</p>
                <div className="mb-3">
                    <label className={styles.fieldLabel}>Enter verification code</label>
                    <div className="d-flex justify-content-center">
                        <div className="d-flex" style={{ gap: '8px', justifyContent: 'space-between', width: '100%' }}>
                            {[0, 1, 2, 3, 4, 5].map(i => (
                                <input
                                    key={i}
                                    type="text"
                                    maxLength="1"
                                    value={smsVerificationCode[i]}
                                    onChange={e => handleSmsCodeChange(i, e)}
                                    onKeyDown={e => handleSmsKeyDown(i, e)}
                                    ref={r => (smsInputRefs.current[i] = r)}
                                    onPaste={handleSmsPaste}
                                    className={'form-control text-center ' + styles.inputField}
                                    style={otpInputStyle}
                                />
                            ))}
                        </div>
                    </div>
                </div>
                <div className="d-flex align-items-center justify-content-between mt-4">
                    <button onClick={handleSmsActivation} disabled={smsVerificationCode.join('').length !== 6 || loading} className={styles.submitBtn}>
                        Activate account {loading && <SpinnerIos16Regular className="rotateLoader ms-1" />}
                    </button>
                    <div className="d-flex flex-column align-items-end">
                        <span role="button" onClick={handleBack} className={styles.secondaryBtnLink}>
                            Back
                        </span>
                        <span
                            role="button"
                            onClick={isResendDisabled ? undefined : handleSendSmsCode}
                            className={styles.secondaryBtnLink}
                            style={{ fontSize: '14px', marginTop: '8px', color: isResendDisabled ? 'var(--Neutral-Gray-6)' : 'var(--Brand-Secondary)', pointerEvents: isResendDisabled ? 'none' : 'auto' }}>
                            {isResendDisabled ? `Resend (${resendSecondsRemaining})` : 'Resend'}
                        </span>
                    </div>
                </div>
                    </>
                )}
            </>
        );
    };

    return (
        <>
            <Pageheader title="LENS - Set up 2FA" />
            <div className={loginStyles.body}>
                <div className={loginStyles.dotedBg}>
                    <div className={loginStyles.formCard}>
                        <div className={loginStyles.formHeader}>
                            <img src={Logo} width={73} alt="lens-logo" />
                        </div>
                        <div className={loginStyles.formBody}>{renderContent()}</div>
                    </div>
                </div>
                <div className={loginStyles.footer}>
                    <a href="https://www.iubenda.com/terms-and-conditions/15531073" target="_blank" rel="noreferrer">
                        Terms of Use
                    </a>
                    <a href="https://www.iubenda.com/privacy-policy/15531073/legal?ifr=true&height=650&newmarkup=yes&an=no" target="_blank" rel="noreferrer">
                        Privacy & Cookies
                    </a>
                </div>
            </div>
        </>
    );
};

export default Setup2FAPage;
